import { MyServicesPicker } from '@/components/my-services-picker';
import { DASHBOARD_SECTIONS, scrollToDashboardSection } from '@/lib/dashboard-sections';
import { Service } from '@/lib/types';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@opaz/ui-components';
import { useNavigate } from '@tanstack/react-router';
import { FilePlus2, FileText, ListChecks, Loader2, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type HomeMobileActionsProps = {
  services: Service[];
  isLoading: boolean;
};

export function HomeMobileActions({ services, isLoading }: HomeMobileActionsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filteredServices = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    if (!normalizedQuery) return services;

    return services.filter((service) => service.title.toLocaleLowerCase().includes(normalizedQuery));
  }, [query, services]);

  const chooseService = (serviceId: string) => {
    setIsOpen(false);
    setQuery('');
    navigate({
      to: '/services/services-catalogue/$id',
      params: { id: serviceId },
    });
  };

  return (
    <>
      <section className="bg-white px-4 py-5 md:hidden" aria-label={t('home.quickActions')}>
        <div className="mx-auto grid max-w-container-960 grid-cols-2 gap-3">
          <Button
            variant="secondary"
            className="h-auto min-h-20 flex-col gap-2 whitespace-normal rounded-xl px-3 py-4"
            onClick={() => {
              void navigate({
                to: '/dashboard/workflows',
                hash: DASHBOARD_SECTIONS.activeApplications,
              }).then(() => {
                scrollToDashboardSection(DASHBOARD_SECTIONS.activeApplications);
              });
            }}
          >
            <ListChecks className="h-6 w-6" aria-hidden="true" />
            {t('home.trackRequests')}
          </Button>
          <MyServicesPicker
            trigger={
              <Button
                variant="secondary"
                className="h-auto min-h-20 flex-col gap-2 whitespace-normal rounded-xl px-3 py-4"
                aria-haspopup="dialog"
              >
                <FileText className="h-6 w-6" aria-hidden="true" />
                {t('home.myServices')}
              </Button>
            }
          />
        </div>
      </section>

      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open);
          if (!open) setQuery('');
        }}
      >
        <DialogTrigger asChild>
          <Button
            className="fixed bottom-24 left-1/2 z-[1040] h-12 -translate-x-1/2 gap-2 whitespace-nowrap rounded-full px-5 shadow-xl md:hidden"
            aria-label={t('home.submitNewRequest')}
          >
            <FilePlus2 className="h-5 w-5" aria-hidden="true" />
            <span>{t('home.startNewService')}</span>
          </Button>
        </DialogTrigger>

        <DialogContent className="bottom-2 top-auto z-[1200] flex max-h-[78vh] w-[calc(100%-1rem)] max-w-none translate-x-[-50%] translate-y-0 flex-col overflow-hidden rounded-2xl p-5 md:hidden">
          <DialogHeader>
            <DialogTitle>{t('home.submitNewRequest')}</DialogTitle>
            <DialogDescription>{t('home.selectServiceToStart')}</DialogDescription>
          </DialogHeader>

          <label className="relative mt-2 block">
            <span className="sr-only">{t('services.searchPlaceholder')}</span>
            <Search
              className="pointer-events-none absolute start-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('services.searchPlaceholder')}
              className="h-11 w-full rounded-lg border border-border bg-white pe-3 ps-10 text-base outline-none focus-visible:ring-2 focus-visible:ring-brand"
            />
          </label>

          <div className="min-h-0 flex-1 overflow-y-auto py-2">
            {isLoading ? (
              <div className="flex min-h-32 items-center justify-center" role="status">
                <Loader2 className="h-7 w-7 animate-spin text-brand" aria-hidden="true" />
                <span className="sr-only">{t('common.loading')}</span>
              </div>
            ) : filteredServices.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">{t('common.emptyMessage')}</p>
            ) : (
              <ul className="divide-y divide-border">
                {filteredServices.map((service) => (
                  <li key={service.documentId}>
                    <button
                      type="button"
                      className="w-full px-2 py-4 text-start font-medium text-link hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                      onClick={() => chooseService(service.documentId)}
                    >
                      {service.title}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
